/**
 * Deep Linking Handler for Capacitor
 * Handles app links like reedle://notes/123 or https://reedle.app/notes/123
 */

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { logger } from './logger';
import { processOpenInFile, processOpenInUrl } from './shareImport';
import { supabase } from './supabase';

let deepLinkInitialized = false;

/**
 * Parse a deep link URL and extract route info
 * @param {string} url - The deep link URL
 * @returns {Object} Parsed route info { path, params }
 */
const parseDeepLink = (url) => {
  try {
    // Handle custom scheme (reedle://path) and universal links (https://reedle.app/path)
    let path = '';

    if (url.startsWith('reedle://')) {
      path = url.replace('reedle://', '/');
    } else if (url.includes('reedle.app')) {
      const urlObj = new URL(url);
      path = urlObj.pathname + urlObj.search;
    } else {
      // Generic URL handling
      const urlObj = new URL(url);
      path = urlObj.pathname + urlObj.search;
    }

    // Extract path segments
    const segments = path.split('/').filter(Boolean);

    return {
      path,
      segments,
      fullUrl: url
    };
  } catch (error) {
    logger.error('Error parsing deep link', { url, error });
    return null;
  }
};

/**
 * Handle incoming deep link
 * @param {string} url - The deep link URL
 */
const handleDeepLink = (url) => {
  // Android ACTION_VIEW: file already stored by MainActivity.storePendingFileShare(),
  // checkAndProcessShares() will pick it up. Ignoring here prevents an infinite reload
  // loop (content:// doesn't match any route → default navigates to '/' → page reload
  // → getLaunchUrl() returns same URI → loop).
  if (url.startsWith('content://')) {
    logger.info('Ignoring content:// URI (handled by native layer)', { url });
    return;
  }

  // OAuth callback: reedle://auth-callback?code=... (PKCE flow from GitHub / browser OAuth)
  if (url.startsWith('reedle://auth-callback')) {
    logger.info('[DeepLink] OAuth callback received', { url });
    try {
      // Extract the code from the URL query string
      const queryString = url.split('?')[1] || '';
      const params = new URLSearchParams(queryString);
      const code = params.get('code');
      if (code) {
        supabase.auth.exchangeCodeForSession(code).then(({ error }) => {
          if (error) {
            logger.error('[DeepLink] Failed to exchange code for session', { error: error.message });
          } else {
            logger.info('[DeepLink] OAuth session established');
          }
        });
        // Close the in-app browser that was opened for OAuth
        import('@capacitor/browser').then(({ Browser }) => Browser.close()).catch(() => {});
      } else {
        logger.warn('[DeepLink] OAuth callback missing code param');
      }
    } catch (error) {
      logger.error('[DeepLink] Error handling OAuth callback', error);
    }
    return;
  }

  // Handle file:// URLs from iOS open-in (PDF/EPUB delivered to Documents/Inbox)
  if (url.startsWith('file://')) {
    const lower = url.toLowerCase();
    const rawName = url.split('/').pop() || 'file';
    const fileName = decodeURIComponent(rawName);
    if (lower.endsWith('.pdf') || lower.includes('.pdf?')) {
      processOpenInFile(url, fileName, 'application/pdf');
    } else if (lower.endsWith('.epub') || lower.includes('.epub?')) {
      processOpenInFile(url, fileName, 'application/epub+zip');
    } else {
      logger.warn('Unsupported file type from open-in', { url });
    }
    return;
  }

  const parsed = parseDeepLink(url);

  if (!parsed) {
    logger.warn('Could not parse deep link', { url });
    return;
  }

  logger.info('Handling deep link', { path: parsed.path });

  const [firstSegment, secondSegment] = parsed.segments;

  switch (firstSegment) {
    case 'notes':
    case 'note':
      // Deep link to specific note: reedle://notes/{noteId}
      if (secondSegment) {
        window.location.href = `/notes/${secondSegment}`;
      } else {
        window.location.href = '/notes';
      }
      break;

    case 'profile':
      // Deep link to profile
      window.location.href = '/profile';
      break;

    case 'home':
    case 'discover':
      // Deep link to home
      window.location.href = '/';
      break;

    case 'settings':
      // Deep link to settings
      window.location.href = '/settings';
      break;

    case 'add': {
      // Open-in URL scheme: reedle://add?url=https://...
      const queryString = parsed.path.includes('?') ? parsed.path.split('?').slice(1).join('?') : '';
      const urlParams = new URLSearchParams(queryString);
      const targetUrl = urlParams.get('url');
      if (targetUrl) {
        processOpenInUrl(targetUrl);
      } else {
        logger.warn('reedle://add called without url param');
      }
      break;
    }

    case 'share':
      // Handle shared content: reedle://share?text=...
      if (secondSegment) {
        // Could be a shared note or content
        window.location.href = `/shared/${secondSegment}`;
      }
      break;

    default:
      // Unknown route, go to home
      logger.warn('Unknown deep link route', { segment: firstSegment });
      window.location.href = '/';
      break;
  }
};

/**
 * Initialize deep linking handler
 * Call this early in app initialization
 */
export const initDeepLinks = async () => {
  if (!Capacitor.isNativePlatform()) {
    logger.info('Deep links not needed on web platform');
    return;
  }

  if (deepLinkInitialized) {
    logger.info('Deep links already initialized');
    return;
  }

  try {
    // Handle app opened via deep link (cold start)
    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url) {
      logger.info('App launched via deep link', { url: launchUrl.url });
      // Delay handling to allow app to fully initialize
      setTimeout(() => handleDeepLink(launchUrl.url), 100);
    }

    // Handle deep links when app is already running (warm start)
    // On iOS cold start, Capacitor fires BOTH getLaunchUrl() AND appUrlOpen for the same URL.
    // Skip the duplicate to prevent double-processing (e.g. two PDF articles from one open-in).
    App.addListener('appUrlOpen', (event) => {
      if (launchUrl?.url && event.url === launchUrl.url) {
        logger.info('Skipping appUrlOpen (already handled via getLaunchUrl)', { url: event.url });
        return;
      }
      logger.info('App URL opened', { url: event.url });
      handleDeepLink(event.url);
    });

    deepLinkInitialized = true;
    logger.info('Deep links initialized');
  } catch (error) {
    logger.error('Error initializing deep links', error);
  }
};

/**
 * Generate a shareable deep link
 * @param {string} type - Type of content (note, profile, etc.)
 * @param {string} id - Content ID
 * @returns {string} Shareable URL
 */
export const generateShareLink = (type, id) => {
  // Use universal link format for better compatibility
  const baseUrl = 'https://reedle.app';

  switch (type) {
    case 'note':
      return `${baseUrl}/notes/${id}`;
    case 'profile':
      return `${baseUrl}/profile/${id}`;
    default:
      return baseUrl;
  }
};
